import { Component, Injector, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, ApisLogging, ApisLoggingService, Channel, ChannelService, Pager } from 'lib-trend-core';
import { debounceTime, finalize, takeUntil } from 'rxjs';
import { ReportApiLoggingsViewComponent } from './report-api-loggings-view/report-api-loggings-view.component';

@Component({
    selector: 'app-report-api-loggings',
    templateUrl: './report-api-loggings.component.html',
    styleUrl: './report-api-loggings.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportApiLoggingsComponent extends AbstractComponent {
  @ViewChild('picker', { static: false }) picker: MatDatepicker<Date>;

  public pager: Pager<ApisLogging> = new Pager<ApisLogging>({ perPage: 10 });

  public listChannel: Array<Channel> = [];
  public listMethod: string[] = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];
  public listStatusCode: Array<{ label: string; value: string }> = [
    { label: this.t('report.apiLoggings.statusSuccess'), value: '2' },
    { label: this.t('report.apiLoggings.statusClientError'), value: '4' },
    { label: this.t('report.apiLoggings.statusServerError'), value: '5' },
  ];

  public labelChannel: string = this.t('report.apiLoggings.labelChannel');
  public labelMethod: string = this.t('report.apiLoggings.labelMethod');
  public labelStatusCode: string = this.t('report.apiLoggings.labelStatus');

  constructor(
    injector: Injector,
    private dialog: MatDialog,
    private apiLoggingService: ApisLoggingService,
    private channelService: ChannelService
  ) {
    super(injector);
    this.setupForm();
  }

  ngOnInit(): void {
    this.getApiLoggings();
    this.getChannelList();
  }

  private setupForm(): void {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 60);

    this.formGroup = this.formBuilder.group({
      channel: [null],
      method: [null],
      statusCode: [null],
      dateRange: this.formBuilder.group({
        dateStart: [thirtyDaysAgo],
        dateEnd: [today],
      }),
    });

    this.formGroup.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(300))
      .subscribe(() => {
        this.pager.page = 1;
        this.getApiLoggings();
      });
  }

  search(term: string): void {
    this.searchString = term;
    this.pager.page = 1;
    this.getApiLoggings();
  }

  loadPage(page: number): void {
    this.pager.page = page;
    this.getApiLoggings();
  }

  searchByChannel(channel: Channel | null): void {
    this.labelChannel = channel ? channel.name : this.t('report.apiLoggings.labelChannel');
    this.formGroup.get('channel').setValue(channel ? channel._id : null);
  }

  searchByMethod(method: string | null): void {
    this.labelMethod = method ?? this.t('report.apiLoggings.labelMethod');
    this.formGroup.get('method').setValue(method);
  }

  searchByStatusCode(status: { label: string; value: string } | null): void {
    this.labelStatusCode = status ? status.label : this.t('report.apiLoggings.labelStatus');
    this.formGroup.get('statusCode').setValue(status ? status.value : null);
  }

  viewItem(item: ApisLogging): void {
    this.dialog.open(ReportApiLoggingsViewComponent, {
      width: '860px',
      maxWidth: '95vw',
      data: { item },
    });
  }

  viewBody(body: object, title?: string): void {
    this.dialog.open(ReportApiLoggingsViewComponent, {
      width: '720px',
      maxWidth: '95vw',
      data: { body, title },
    });
  }

  private getApiLoggings(): void {
    this.loadingContent = true;

    const formValues = this.formGroup.getRawValue();
    const filters: Record<string, unknown> = {
      statusCode: formValues.statusCode,
      method: formValues.method,
      channel: formValues.channel,
      user: formValues.user,
      ...(formValues.dateRange?.dateStart && formValues.dateRange?.dateEnd
        ? {
          dateStart: formValues.dateRange.dateStart.getTime(),
          dateEnd: formValues.dateRange.dateEnd.getTime(),
        }
        : {}),
    };

    this.searchParams = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, unknown>);

    this.apiLoggingService.getLoggingsReport(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(takeUntil(this.destroy$), finalize(() => (this.loadingContent = false)))
      .subscribe({
        next: (pager: Pager<ApisLogging>) => {
          this.pager = pager;
        },
      });
  }

  private getChannelList(): void {
    this.channelService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channels: Array<Channel>) => {
          this.listChannel = channels;
        },
      });
  }

  onPeriodChange(event: any) {
    const today = new Date();
    const period = parseInt(event.value, 10);

    if (!isNaN(period)) {
      this.formGroup.get('dateRange')?.patchValue({
        dateStart: new Date(today.setDate(today.getDate() - period)),
        dateEnd: new Date(),
      });
      this.picker?.close();
    }
  }

  onPickerClose() {
    const dateStart = this.formGroup.get('dateRange')?.get('dateStart')?.value;
    const dateEnd = this.formGroup.get('dateRange')?.get('dateEnd')?.value;
  }
}
